import { useEffect, useState, type ReactNode } from "react";
import { ChevronDown, Plus, Trash2 } from "lucide-react";
import { IdentityProviderMark } from "@ncp/assistant-sdk";
import { useLocale, useTranslation } from "@ncp/i18n";
import { Button, Field, Input, NativeSelect, Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle, Switch, cn } from "@ncp/ui";
import { KeyValueRows } from "../connectors/fields";
import { useSecrets } from "../settings/api";
import { useWorkspaces } from "../workspaces/api";
import { useRoles } from "./api";
import { IDENTITY_PROVIDER_KINDS, type IdentityProvider, type IdentityProviderInput, type IdentityProviderKind, type IdpRoleMapping, type IdpWorkspaceMapping } from "./identityProviders";

type ClaimKey = "subjectClaim" | "emailClaim" | "nameClaim" | "groupsClaim";
const CLAIM_KEYS: ClaimKey[] = ["subjectClaim", "emailClaim", "nameClaim", "groupsClaim"];
const CLAIM_DEFAULTS: Record<IdentityProviderKind, Record<ClaimKey, string>> = {
  EntraId: { subjectClaim: "oid", emailClaim: "preferred_username", nameClaim: "name", groupsClaim: "groups" },
  Oidc: { subjectClaim: "sub", emailClaim: "email", nameClaim: "name", groupsClaim: "groups" },
  IdentityServer: { subjectClaim: "sub", emailClaim: "email", nameClaim: "name", groupsClaim: "role" },
};
const ISSUER_PLACEHOLDER: Record<IdentityProviderKind, string> = {
  EntraId: "https://login.microsoftonline.com/<tenant-id>/v2.0",
  Oidc: "https://idp.example.gov.ae",
  IdentityServer: "https://identity.moet.gov.ae",
};
const KEY_PATTERN = /^[a-z][a-z0-9-]{1,63}$/;
const slug = (v: string) => v.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^[^a-z]+|-+$/g, "").slice(0, 64);
const text = (v: string | null | undefined) => (v?.trim() ? v.trim() : null);

type Form = IdentityProviderInput & { key: string };

function blank(kind: IdentityProviderKind = "EntraId"): Form {
  return {
    key: "", name: "", nameAr: "", kind, issuer: "", authorizationEndpoint: "", tokenEndpoint: "", userInfoEndpoint: "", jwksUri: "", endSessionEndpoint: "",
    clientId: "", clientSecretRef: "", scopes: "openid profile email", usePkce: true, ...CLAIM_DEFAULTS[kind], nameArClaim: "",
    roleMappings: [], workspaceMappings: [], defaultRoleKeys: [], justInTimeProvisioning: true, syncOnSignIn: true, linkByEmail: true,
    allowedEmailDomains: [], extraAuthorizeParams: {}, iconUrl: "", enabled: false, simulated: false,
    mfaWithinMinutes: kind === "EntraId" ? 720 : null, profileSync: kind === "EntraId", arabicNameAttribute: "", apps: [],
  };
}

function formOf(p: IdentityProvider): Form {
  return {
    key: p.key, name: p.name, nameAr: p.nameAr ?? "", kind: p.kind, issuer: p.issuer,
    authorizationEndpoint: p.authorizationEndpoint ?? "", tokenEndpoint: p.tokenEndpoint ?? "", userInfoEndpoint: p.userInfoEndpoint ?? "", jwksUri: p.jwksUri ?? "", endSessionEndpoint: p.endSessionEndpoint ?? "",
    clientId: p.clientId, clientSecretRef: p.clientSecretRef ?? "", scopes: p.scopes, usePkce: p.usePkce,
    subjectClaim: p.subjectClaim, emailClaim: p.emailClaim ?? "", nameClaim: p.nameClaim ?? "", nameArClaim: p.nameArClaim ?? "", groupsClaim: p.groupsClaim ?? "",
    roleMappings: p.roleMappings.map((m) => ({ ...m })), workspaceMappings: p.workspaceMappings.map((m) => ({ ...m })), defaultRoleKeys: [...p.defaultRoleKeys],
    justInTimeProvisioning: p.justInTimeProvisioning, syncOnSignIn: p.syncOnSignIn, linkByEmail: p.linkByEmail,
    allowedEmailDomains: [...p.allowedEmailDomains], extraAuthorizeParams: { ...p.extraAuthorizeParams }, iconUrl: p.iconUrl ?? "", enabled: p.enabled, simulated: p.simulated,
    mfaWithinMinutes: p.mfaWithinMinutes ?? null, profileSync: p.profileSync, arabicNameAttribute: p.arabicNameAttribute ?? "", apps: [...(p.apps ?? [])],
  };
}

function inputOf(f: Form, domains: string): IdentityProviderInput {
  return {
    name: f.name.trim(), nameAr: text(f.nameAr), kind: f.kind, issuer: f.issuer.trim(),
    authorizationEndpoint: text(f.authorizationEndpoint), tokenEndpoint: text(f.tokenEndpoint), userInfoEndpoint: text(f.userInfoEndpoint), jwksUri: text(f.jwksUri), endSessionEndpoint: text(f.endSessionEndpoint),
    clientId: f.clientId.trim(), clientSecretRef: text(f.clientSecretRef), scopes: f.scopes.trim(), usePkce: f.usePkce,
    subjectClaim: f.subjectClaim.trim(), emailClaim: text(f.emailClaim), nameClaim: text(f.nameClaim), nameArClaim: text(f.nameArClaim), groupsClaim: text(f.groupsClaim),
    roleMappings: f.roleMappings.filter((m) => m.value.trim() && m.roleKey).map((m) => ({ claim: text(m.claim), value: m.value.trim(), label: text(m.label), roleKey: m.roleKey })),
    workspaceMappings: f.workspaceMappings.filter((m) => m.value.trim() && m.workspaceKey).map((m) => ({ claim: text(m.claim), value: m.value.trim(), label: text(m.label), workspaceKey: m.workspaceKey, isOwner: m.isOwner })),
    defaultRoleKeys: f.defaultRoleKeys,
    justInTimeProvisioning: f.justInTimeProvisioning, syncOnSignIn: f.syncOnSignIn, linkByEmail: f.linkByEmail,
    allowedEmailDomains: domains.split(/[\s,;]+/).map((d) => d.trim().toLowerCase().replace(/^@/, "")).filter(Boolean),
    extraAuthorizeParams: f.extraAuthorizeParams, iconUrl: text(f.iconUrl), enabled: f.enabled, simulated: f.simulated,
    mfaWithinMinutes: f.mfaWithinMinutes && f.mfaWithinMinutes > 0 ? Math.round(f.mfaWithinMinutes) : null,
    profileSync: f.kind === "EntraId" && f.profileSync,
    arabicNameAttribute: f.kind === "EntraId" && f.profileSync ? text(f.arabicNameAttribute) : null,
    apps: f.apps,
  };
}

function Section({ title, hint, children }: { title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="space-y-3 border-t border-sand-100 pt-5 first:border-t-0 first:pt-0">
      <div>
        <h3 className="text-sm font-semibold text-foreground">{title}</h3>
        {hint ? <p className="mt-0.5 text-xs leading-5 text-muted-foreground">{hint}</p> : null}
      </div>
      {children}
    </section>
  );
}

function Toggle({ label, hint, checked, onChange }: { label: string; hint?: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-start justify-between gap-3 rounded-lg border border-sand-200 px-3 py-2.5 text-sm">
      <span className="min-w-0">
        <span className="block font-medium">{label}</span>
        {hint ? <span className="mt-0.5 block text-xs leading-5 text-muted-foreground">{hint}</span> : null}
      </span>
      <Switch checked={checked} onCheckedChange={onChange} className="mt-0.5 shrink-0" />
    </label>
  );
}

interface Option { value: string; label: string }

/** Claim value → target rows shared by role and workspace mappings; `owner` adds the workspace owner switch. */
function MappingRows<T extends { claim?: string | null; value: string; label?: string | null }>({ rows, onChange, targetOf, withTarget, options, targetLabel, groupsClaim, addLabel, blankRow, owner }: {
  rows: T[];
  onChange: (rows: T[]) => void;
  targetOf: (row: T) => string;
  withTarget: (row: T, target: string) => T;
  options: Option[];
  targetLabel: string;
  groupsClaim: string;
  addLabel: string;
  blankRow: T;
  owner?: { of: (row: T) => boolean; with: (row: T, v: boolean) => T };
}) {
  const { t } = useTranslation();
  const update = (i: number, row: T) => onChange(rows.map((r, j) => (j === i ? row : r)));
  const grid = owner ? "sm:grid-cols-[6.5rem_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_3.5rem_2rem]" : "sm:grid-cols-[6.5rem_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_2rem]";
  return (
    <div className="space-y-2">
      {rows.length > 0 ? (
        <div className={cn("hidden gap-2 px-0.5 text-[11px] font-medium uppercase tracking-wider text-sand-500 sm:grid", grid)}>
          <span>{t("admin:identity.providers.fields.claim")}</span>
          <span>{t("admin:identity.providers.fields.value")}</span>
          <span>{t("admin:identity.providers.fields.label")}</span>
          <span>{targetLabel}</span>
          {owner ? <span className="text-center">{t("admin:identity.providers.fields.owner")}</span> : null}
          <span />
        </div>
      ) : null}
      {rows.map((row, i) => {
        const target = targetOf(row);
        const unknown = target && !options.some((o) => o.value === target);
        return (
          <div key={i} className={cn("grid grid-cols-1 items-center gap-2 rounded-lg border border-sand-100 p-2 sm:border-0 sm:p-0", grid)}>
            <Input value={row.claim ?? ""} dir="ltr" className="h-9 font-mono text-xs" placeholder={groupsClaim || t("admin:identity.providers.fields.claimPlaceholder")} aria-label={t("admin:identity.providers.fields.claim")}
              onChange={(e) => update(i, { ...row, claim: e.target.value })} />
            <Input value={row.value} dir="ltr" className="h-9 font-mono text-xs" placeholder={t("admin:identity.providers.fields.valuePlaceholder")} aria-label={t("admin:identity.providers.fields.value")}
              onChange={(e) => update(i, { ...row, value: e.target.value })} />
            <Input value={row.label ?? ""} className="h-9 text-xs" placeholder={t("admin:identity.providers.fields.labelPlaceholder")} aria-label={t("admin:identity.providers.fields.label")}
              onChange={(e) => update(i, { ...row, label: e.target.value })} />
            <NativeSelect value={target} className={cn("h-9 text-xs", unknown && "border-danger-300 text-danger-700")} aria-label={targetLabel} onChange={(e) => update(i, withTarget(row, e.target.value))}>
              <option value="">{t("admin:identity.providers.fields.choose")}</option>
              {unknown ? <option value={target}>{target}</option> : null}
              {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </NativeSelect>
            {owner ? <span className="flex justify-center"><Switch checked={owner.of(row)} onCheckedChange={(v) => update(i, owner.with(row, v))} aria-label={t("admin:identity.providers.fields.owner")} /></span> : null}
            <Button size="icon-sm" variant="ghost" className="justify-self-end text-danger-600" aria-label={t("admin:common.delete")} onClick={() => onChange(rows.filter((_, j) => j !== i))}><Trash2 /></Button>
          </div>
        );
      })}
      <Button size="sm" variant="ghost" onClick={() => onChange([...rows, { ...blankRow }])}><Plus /> {addLabel}</Button>
    </div>
  );
}

/** Create or edit a staff identity provider: connection, claims, group → role / workspace mappings and the sign-in policy. */
export function IdentityProviderEditor({ open, provider, busy, onOpenChange, onSubmit }: {
  open: boolean;
  provider: IdentityProvider | null;
  busy: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (key: string, input: IdentityProviderInput, create: boolean) => void;
}) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const roles = useRoles();
  const workspaces = useWorkspaces();
  const secrets = useSecrets();
  const [form, setForm] = useState<Form>(() => blank());
  const [domains, setDomains] = useState("");
  const [keyTouched, setKeyTouched] = useState(false);
  const [advanced, setAdvanced] = useState(false);

  useEffect(() => {
    if (!open) return;
    const next = provider ? formOf(provider) : blank();
    setForm(next);
    setDomains(next.allowedEmailDomains.join(", "));
    setKeyTouched(!!provider);
    setAdvanced(!!provider && !!(provider.authorizationEndpoint || provider.tokenEndpoint || provider.userInfoEndpoint || provider.jwksUri || provider.endSessionEndpoint));
  }, [open, provider]);

  const set = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));
  // Claims still at the previous type's defaults follow the new type; anything typed by hand stays.
  const changeKind = (kind: IdentityProviderKind) => setForm((f) => {
    const next = { ...f, kind };
    for (const k of CLAIM_KEYS) if ((f[k] ?? "") === CLAIM_DEFAULTS[f.kind][k]) next[k] = CLAIM_DEFAULTS[kind][k];
    return next;
  });

  const p = "admin:identity.providers";
  const roleOptions: Option[] = (roles.data ?? []).filter((r) => r.enabled && r.key !== "Applicant" && r.key !== "Representative")
    .map((r) => ({ value: r.key, label: locale === "ar" && r.nameAr ? r.nameAr : t(`admin:identity.roles.builtInNames.${r.key}`, { defaultValue: r.name }) }));
  const workspaceOptions: Option[] = (workspaces.data ?? []).map((w) => ({ value: w.key, label: locale === "ar" && w.nameAr ? w.nameAr : w.name }));
  const secretList = secrets.data?.secrets ?? [];
  const secretRef = form.clientSecretRef ?? "";
  const valid = !!form.name.trim() && !!form.issuer.trim() && !!form.clientId.trim() && KEY_PATTERN.test(form.key) && /\bopenid\b/.test(form.scopes);
  const groupsClaim = form.groupsClaim?.trim() || "groups";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent width="max-w-3xl">
        <SheetHeader className="pe-12">
          <SheetTitle className="flex items-center gap-2"><IdentityProviderMark kind={form.kind} iconUrl={text(form.iconUrl)} className="size-5" /> {provider ? t(`${p}.editProvider`) : t(`${p}.newProvider`)}</SheetTitle>
          <SheetDescription>{t(`${p}.dialogHint`)}</SheetDescription>
        </SheetHeader>
        <SheetBody className="space-y-5">
          <Section title={t(`${p}.sections.general`)}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t(`${p}.fields.kind`)}>
                <NativeSelect value={form.kind} onChange={(e) => changeKind(e.target.value as IdentityProviderKind)}>
                  {IDENTITY_PROVIDER_KINDS.map((k) => <option key={k} value={k}>{t(`${p}.kinds.${k}`)}</option>)}
                </NativeSelect>
              </Field>
              <Field label={t("admin:common.key")} required>
                <Input value={form.key} disabled={!!provider} dir="ltr" className="font-mono" placeholder="entra-moet" onChange={(e) => { setKeyTouched(true); set("key", slug(e.target.value)); }} />
              </Field>
              <Field label={t("admin:common.name")} required>
                <Input value={form.name} placeholder="Microsoft Entra ID" onChange={(e) => { const name = e.target.value; setForm((f) => ({ ...f, name, key: keyTouched ? f.key : slug(name) })); }} />
              </Field>
              <Field label={t("admin:common.nameAr")}>
                <Input value={form.nameAr ?? ""} dir="rtl" onChange={(e) => set("nameAr", e.target.value)} />
              </Field>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle label={t("admin:common.enabled")} hint={t(`${p}.fields.enabledHint`)} checked={form.enabled} onChange={(v) => set("enabled", v)} />
              <Toggle label={t(`${p}.simulated`)} hint={t(`${p}.simulatedHint`)} checked={form.simulated} onChange={(v) => set("simulated", v)} />
            </div>
          </Section>

          <Section title={t(`${p}.sections.connection`)}>
            <Field label={t(`${p}.fields.issuer`)} hint={t(`${p}.fields.issuerHint`)} required>
              <Input value={form.issuer} dir="ltr" className="font-mono text-xs" placeholder={form.simulated ? "{{simulators}}/entra/<tenant-id>/v2.0" : ISSUER_PLACEHOLDER[form.kind]} onChange={(e) => set("issuer", e.target.value)} />
            </Field>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t(`${p}.fields.clientId`)} required>
                <Input value={form.clientId} dir="ltr" className="font-mono text-xs" placeholder="00000000-0000-0000-0000-000000000000" onChange={(e) => set("clientId", e.target.value)} />
              </Field>
              <Field label={t(`${p}.fields.clientSecret`)} hint={t(`${p}.fields.clientSecretHint`)}>
                <NativeSelect value={secretRef} onChange={(e) => set("clientSecretRef", e.target.value)}>
                  <option value="">{t(`${p}.fields.noSecret`)}</option>
                  {secretRef && !secretList.some((s) => s.reference === secretRef) ? <option value={secretRef}>{secretRef}</option> : null}
                  {secretList.map((s) => <option key={s.key} value={s.reference}>{s.key} ({s.hint})</option>)}
                </NativeSelect>
              </Field>
              <Field label={t(`${p}.fields.scopes`)} hint={t(`${p}.fields.scopesHint`)}>
                <Input value={form.scopes} dir="ltr" className="font-mono text-xs" onChange={(e) => set("scopes", e.target.value)} />
              </Field>
              <div className="self-end">
                <Toggle label={t(`${p}.fields.pkce`)} hint={t(`${p}.fields.pkceHint`)} checked={form.usePkce} onChange={(v) => set("usePkce", v)} />
              </div>
            </div>
            <Field label={t(`${p}.fields.extraParams`)} hint={t(`${p}.fields.extraParamsHint`)}>
              <KeyValueRows value={form.extraAuthorizeParams} onChange={(v) => set("extraAuthorizeParams", v)} readOnly={false} keyPlaceholder="prompt" valuePlaceholder="select_account" />
            </Field>
            <div className="rounded-lg border border-sand-200">
              <button type="button" className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm" aria-expanded={advanced} onClick={() => setAdvanced((v) => !v)}>
                <span><span className="font-medium">{t(`${p}.fields.advanced`)}</span><span className="block text-xs text-muted-foreground">{t(`${p}.fields.advancedHint`)}</span></span>
                <ChevronDown className={cn("size-4 shrink-0 text-sand-500 transition-transform", advanced && "rotate-180")} />
              </button>
              {advanced ? (
                <div className="grid gap-3 border-t border-sand-100 p-3 sm:grid-cols-2">
                  {(["authorizationEndpoint", "tokenEndpoint", "userInfoEndpoint", "jwksUri", "endSessionEndpoint"] as const).map((k) => (
                    <Field key={k} label={t(`${p}.fields.${k}`)}>
                      <Input value={form[k] ?? ""} dir="ltr" className="font-mono text-xs" placeholder="https://" onChange={(e) => set(k, e.target.value)} />
                    </Field>
                  ))}
                </div>
              ) : null}
            </div>
          </Section>

          <Section title={t(`${p}.sections.claims`)}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Field label={t(`${p}.fields.subjectClaim`)} hint={t(`${p}.fields.subjectClaimHint`)}>
                <Input value={form.subjectClaim} dir="ltr" className="font-mono text-xs" onChange={(e) => set("subjectClaim", e.target.value)} />
              </Field>
              <Field label={t(`${p}.fields.groupsClaim`)} hint={t(`${p}.fields.groupsClaimHint`)}>
                <Input value={form.groupsClaim ?? ""} dir="ltr" className="font-mono text-xs" onChange={(e) => set("groupsClaim", e.target.value)} />
              </Field>
              <Field label={t(`${p}.fields.emailClaim`)}>
                <Input value={form.emailClaim ?? ""} dir="ltr" className="font-mono text-xs" onChange={(e) => set("emailClaim", e.target.value)} />
              </Field>
              <Field label={t(`${p}.fields.nameClaim`)}>
                <Input value={form.nameClaim ?? ""} dir="ltr" className="font-mono text-xs" onChange={(e) => set("nameClaim", e.target.value)} />
              </Field>
              <Field label={t(`${p}.fields.nameArClaim`)}>
                <Input value={form.nameArClaim ?? ""} dir="ltr" className="font-mono text-xs" onChange={(e) => set("nameArClaim", e.target.value)} />
              </Field>
            </div>
          </Section>

          <Section title={t(`${p}.sections.roles`)} hint={t(`${p}.fields.rolesHint`)}>
            <MappingRows<IdpRoleMapping>
              rows={form.roleMappings}
              onChange={(rows) => set("roleMappings", rows)}
              targetOf={(r) => r.roleKey}
              withTarget={(r, roleKey) => ({ ...r, roleKey })}
              options={roleOptions}
              targetLabel={t(`${p}.fields.role`)}
              groupsClaim={groupsClaim}
              addLabel={t(`${p}.fields.addRoleMapping`)}
              blankRow={{ claim: "", value: "", label: "", roleKey: "" }}
            />
            <Field label={t(`${p}.fields.defaultRoles`)} hint={t(`${p}.fields.defaultRolesHint`)}>
              <div className="flex flex-wrap gap-1.5">
                {roleOptions.map((o) => {
                  const on = form.defaultRoleKeys.includes(o.value);
                  return (
                    <button key={o.value} type="button" aria-pressed={on}
                      className={cn("rounded-full border px-2.5 py-1 text-xs transition-colors", on ? "border-aegold-400 bg-aegold-50 text-aegold-800" : "border-sand-200 text-sand-700 hover:border-aegold-300")}
                      onClick={() => set("defaultRoleKeys", on ? form.defaultRoleKeys.filter((k) => k !== o.value) : [...form.defaultRoleKeys, o.value])}>
                      {o.label}
                    </button>
                  );
                })}
              </div>
            </Field>
          </Section>

          <Section title={t(`${p}.sections.workspaces`)} hint={t(`${p}.fields.workspacesHint`)}>
            <MappingRows<IdpWorkspaceMapping>
              rows={form.workspaceMappings}
              onChange={(rows) => set("workspaceMappings", rows)}
              targetOf={(r) => r.workspaceKey}
              withTarget={(r, workspaceKey) => ({ ...r, workspaceKey })}
              options={workspaceOptions}
              targetLabel={t(`${p}.fields.workspace`)}
              groupsClaim={groupsClaim}
              addLabel={t(`${p}.fields.addWorkspaceMapping`)}
              blankRow={{ claim: "", value: "", label: "", workspaceKey: "", isOwner: false }}
              owner={{ of: (r) => r.isOwner, with: (r, isOwner) => ({ ...r, isOwner }) }}
            />
          </Section>

          <Section title={t(`${p}.sections.policy`)}>
            <div className="grid gap-3 sm:grid-cols-2">
              <Toggle label={t(`${p}.fields.jit`)} hint={t(`${p}.fields.jitHint`)} checked={form.justInTimeProvisioning} onChange={(v) => set("justInTimeProvisioning", v)} />
              <Toggle label={t(`${p}.fields.linkByEmail`)} hint={t(`${p}.fields.linkByEmailHint`)} checked={form.linkByEmail} onChange={(v) => set("linkByEmail", v)} />
              <Toggle label={t(`${p}.fields.sync`)} hint={t(`${p}.fields.syncHint`)} checked={form.syncOnSignIn} onChange={(v) => set("syncOnSignIn", v)} />
              <Field label={t(`${p}.fields.allowedDomains`)} hint={t(`${p}.fields.allowedDomainsHint`)}>
                <Input value={domains} dir="ltr" className="font-mono text-xs" placeholder="moet.gov.ae" onChange={(e) => setDomains(e.target.value)} />
              </Field>
              <Toggle label={t(`${p}.fields.appEconic`)} hint={t(`${p}.fields.appsHint`)} checked={form.apps.includes("econic")}
                onChange={(v) => set("apps", v ? [...form.apps.filter((a) => a !== "econic"), "econic"] : form.apps.filter((a) => a !== "econic"))} />
              <Toggle label={t(`${p}.fields.appNcp`)} hint={t(`${p}.fields.appsHint`)} checked={form.apps.includes("ncp")}
                onChange={(v) => set("apps", v ? [...form.apps.filter((a) => a !== "ncp"), "ncp"] : form.apps.filter((a) => a !== "ncp"))} />
              <Field label={t(`${p}.fields.mfaWithin`)} hint={t(`${p}.fields.mfaWithinHint`)}>
                <Input type="number" min={1} max={1440} value={form.mfaWithinMinutes ?? ""} dir="ltr" className="font-mono text-xs" placeholder="720"
                  onChange={(e) => set("mfaWithinMinutes", e.target.value === "" ? null : Number(e.target.value))} />
              </Field>
              {form.kind === "EntraId" ? (
                <>
                  <Toggle label={t(`${p}.fields.profileSync`)} hint={t(`${p}.fields.profileSyncHint`)} checked={form.profileSync} onChange={(v) => set("profileSync", v)} />
                  {form.profileSync ? (
                    <Field label={t(`${p}.fields.arabicName`)} hint={t(`${p}.fields.arabicNameHint`)}>
                      <Input value={form.arabicNameAttribute ?? ""} dir="ltr" className="font-mono text-xs" placeholder="extensionAttribute1" onChange={(e) => set("arabicNameAttribute", e.target.value)} />
                    </Field>
                  ) : null}
                </>
              ) : null}
            </div>
          </Section>
        </SheetBody>
        <div className="flex items-center justify-end gap-2 border-t border-sand-200 px-6 py-4">
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t("admin:common.cancel")}</Button>
          <Button variant="gold" loading={busy} disabled={!valid} onClick={() => onSubmit(form.key, inputOf(form, domains), !provider)}>{t("admin:common.save")}</Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
